import logo from "@/assets/logo.png";
import TabBadges from "@/components/Table/MrtCaseFilters/TabBadges.tsx";
import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import BarChartIcon from "@mui/icons-material/BarChart";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FactoryIcon from "@mui/icons-material/Factory";
import GroupsIcon from "@mui/icons-material/Groups";
import HouseIcon from "@mui/icons-material/House";
import {
    Box,
    Collapse,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import { FileText, History } from "lucide-react";
import { Fragment, type ReactNode, useState } from "react";
import { Link } from "react-router-dom";

type NavigationItem = {
    href: string;
    label: string;
    icon?: ReactNode;
    children?: NavigationItem[];
    allowedPermissions?: string[];
};

const navigationItems: NavigationItem[] = [
    {
        label: "Dashboard",
        href: "/",
        icon: <HouseIcon />,
    },
    {
        label: "People",
        href: "/people",
        icon: <GroupsIcon />,
    },
    {
        label: "Places",
        href: "/places",
        icon: <FactoryIcon />,
    },
    {
        label: "Cases",
        href: "/cases",
        icon: <BusinessCenterIcon />,
    },
    {
        label: "Notification History",
        href: "/notification-history",
        icon: <History size={20} />,
    },
    {
        label: "Reports",
        href: "/reports",
        icon: <BarChartIcon />,
        allowedPermissions: [
            "view:report-sol",
            "view:report-referrals",
            "view:report-demand-dates",
            "view:report-last-touch",
        ],
        children: [
            {
                label: "Last Client Contact",
                href: "/reports/last-touch",
                allowedPermissions: ["view:report-last-touch"],
            },
        ],
    },
    {
        label: "Admin",
        href: "/admin",
        icon: <BarChartIcon />,
        allowedPermissions: [
            "create:employee",
            "delete:employee",
            "edit:employee",
            "create:task-template",
            "edit:task-template",
            "delete:task-template",
            "create:case-template",
            "edit:case-template",
            "delete:case-template",
        ],
        children: [
            {
                label: "User Management",
                href: "/admin/user-management",
                allowedPermissions: ["create:employee", "delete:employee", "edit:employee"],
            },
            {
                label: "Templates",
                href: "/admin/templates",
                allowedPermissions: [
                    "create:task-template",
                    "edit:task-template",
                    "delete:task-template",
                    "create:case-template",
                    "edit:case-template",
                    "delete:case-template",
                ],
            },
            {
                label: "Document Templates",
                href: "/admin/document-templates",
                icon: <FileText size={20} />,
                allowedPermissions: ["edit:doc-header"],
            },
        ],
    },
];

const DrawerContent = (): ReactNode => {
    const { hasAnyPermission } = useEmployee();
    const [open, setOpen] = useState<Record<string, boolean>>({});

    const handleClick = (label: string) => {
        if (open[label]) {
            setOpen((prev) => ({ ...prev, [label]: false }));
        } else {
            setOpen((prev) => ({ ...prev, [label]: true }));
        }
    };

    return (
        <>
            <Box
                sx={{
                    textAlign: "center",
                }}
            >
                <Box component="img" src={logo} maxHeight={80} alt="Logo" sx={{ my: 2 }} />
            </Box>
            <Divider variant="middle" />

            <List>
                {navigationItems.map((item) => {
                    if (item.allowedPermissions && !hasAnyPermission(item.allowedPermissions)) {
                        return null;
                    }

                    if (item.children) {
                        return (
                            <Fragment key={item.href}>
                                <ListItemButton
                                    onClick={() => {
                                        handleClick(item.label);
                                    }}
                                >
                                    <ListItemText primary={item.label} />
                                    {open[item.label] ? <ExpandLess /> : <ExpandMore />}
                                </ListItemButton>
                                <Collapse in={open[item.label]} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {item.children.map((childItem) => {
                                            if (
                                                childItem.allowedPermissions &&
                                                !hasAnyPermission(childItem.allowedPermissions)
                                            ) {
                                                return null;
                                            }

                                            return (
                                                <ListItem
                                                    key={childItem.href}
                                                    disablePadding
                                                    sx={{ ml: 2 }}
                                                >
                                                    <ListItemButton
                                                        component={Link}
                                                        to={childItem.href}
                                                    >
                                                        {childItem.icon && (
                                                            <ListItemIcon sx={{ color: "#fff" }}>
                                                                {childItem.icon}
                                                            </ListItemIcon>
                                                        )}
                                                        <ListItemText primary={childItem.label} />
                                                    </ListItemButton>
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </Collapse>
                            </Fragment>
                        );
                    }

                    return (
                        <ListItem key={item.href} disablePadding>
                            <ListItemButton component={Link} to={item.href}>
                                {item.icon && (
                                    <ListItemIcon sx={{ color: "#fff" }}>{item.icon}</ListItemIcon>
                                )}
                                <ListItemText primary={item.label} />
                                {item.label === "Dashboard" && <TabBadges />}
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        </>
    );
};

export default DrawerContent;
